import { useEffect, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18n-lite'

import {
  sendMessageToParent,
  useDisplayUserError
} from '@yes.technology/react-toolkit'

import { useAppDispatch } from 'state/store'

import { Step, InteractionModel } from 'types/shared'

import { validateStepCallback } from '../../../../utils/interactionModel'
import { interactionModelOperations } from '../../redux'
import { getStepErrorMessages, showError } from '../../utils/validation'
import useValidateStep from '../useValidateStep/useValidateStep'
import { flatValuesToOldActivities } from '../../utils/flatValuesToOldActivities'
import useSequenceRules from './useSequenceRules'
import useSaveDraftInteraction from '../useSaveDraftInteraction'
import * as googleTagManager from 'modules/shared/GTM'
import { useSearchParams } from 'react-router-dom'
import useInterpolateUrl from '../useInterpolateUrl'

import useSaveAndFinish from './useSaveAndFinish'

const useStep = (interactionModel: InteractionModel, onSave: () => void) => {
  const { t } = useTranslation()

  const dispatch = useAppDispatch()

  const interpolateUrl = useInterpolateUrl()

  const [currentStepIndex, setCurrentStepIndex] = useState(1)
  const [isLoadedSteps, setIsLoadedSteps] = useState(false)

  const activityModels = useMemo(
    () => interactionModel.flatData?.activity_models || {},
    [interactionModel.flatData?.activity_models]
  )

  const activityValues = useMemo(
    () => interactionModel.flatData?.activity_values || {},
    [interactionModel.flatData?.activity_values]
  )

  const { hiddenStepIds, hasPendingDecisionModels } = useSequenceRules({
    activityModels,
    activityValues
  })

  const steps: Step[] = useMemo(() => {
    const allSteps = interactionModel.flatData?.steps || []

    return hiddenStepIds.length
      ? allSteps.filter(({ uuid }) => !hiddenStepIds.includes(uuid))
      : allSteps
  }, [hiddenStepIds, interactionModel.flatData?.steps])

  const totalSteps = steps.length + 1

  const initialStepUuid = interactionModel.flatData?.initial_step_uuid
  const initialStepIndex =
    steps.findIndex((step) => step.uuid === initialStepUuid) + 1

  const {
    saveAndFinishAfterDraftUpdates,
    isExecutingAutomations,
    uuidInteraction,
    isInteractionFinished,
    isSaving
  } = useSaveAndFinish(interactionModel, onSave, hiddenStepIds)

  useEffect(() => {
    setCurrentStepIndex(initialStepIndex || 1)
  }, [initialStepIndex])

  useEffect(() => {
    if (totalSteps === 1) return

    setIsLoadedSteps(true)
  }, [totalSteps])

  useEffect(() => {
    if (
      isInteractionFinished &&
      !interactionModel?.isFetching &&
      uuidInteraction &&
      !isExecutingAutomations
    ) {
      const informationCards = interactionModel.flatData?.information_cards
      const hasConclusionCard = informationCards?.some(
        (card) => card.display_on === 'interaction-finished'
      )

      if (!hasConclusionCard) {
        sendMessageToParent(
          interactionModel.workflowUuid || '',
          uuidInteraction
        )
      }
    }
  }, [
    isInteractionFinished,
    interactionModel,
    uuidInteraction,
    isExecutingAutomations
  ])

  const { displayUserError } = useDisplayUserError()

  const { validateStep } = useValidateStep()

  const [searchParams] = useSearchParams()
  const draftInteractionUuid = searchParams.get('draft_interaction_uuid') || ''

  const { createDraftInteraction, updateDraftInteraction } =
    useSaveDraftInteraction()

  const afterNextButton = async (step: number) => {
    const stepActivities = steps[currentStepIndex - 1]?.activity_models

    if (!stepActivities) {
      return
    }

    const { invalidActivities, updatedActivityValues } = await validateStep({
      activityModels,
      activityValues,
      stepActivities
    })

    dispatch(
      interactionModelOperations.updateActivities(
        flatValuesToOldActivities(updatedActivityValues)
      )
    )

    const currentStep = steps[currentStepIndex - 1]

    getStepErrorMessages({
      invalidActivities,
      stepName: currentStep.des,
      t
    }).forEach((error) => showError({ error }))

    if (invalidActivities.length > 0) {
      return
    }

    if (currentStep?.validation_step_callback) {
      const interpolatedCallback = interpolateUrl(
        currentStep.validation_step_callback
      )

      const responseValidationCallback = await validateStepCallback({
        activityValues,
        stepUuid: currentStep.uuid,
        endpoint: interpolatedCallback
      })

      if (!responseValidationCallback?.success) {
        displayUserError({ error: responseValidationCallback })

        return
      }
    }

    if (draftInteractionUuid) updateDraftInteraction({ interactionModel })
    else createDraftInteraction({ interactionModel })

    // Send interaction_progress event to Google Tag Manager
    googleTagManager.setDataLayer({
      event: googleTagManager.Events.INTERACTION_PROGRESS,
      current_step: step
    })

    setCurrentStepIndex(step)
  }

  const changeStep = (step: number) => {
    const finishingInteraction = step > totalSteps
    const isAnyStepWithValidation = step > currentStepIndex

    // clicou no botão Salvar e Ativar
    if (finishingInteraction && interactionModel.flatData) {
      // Update interaction_progress event in Google Tag Manager
      googleTagManager.setDataLayer({
        event: googleTagManager.Events.INTERACTION_PROGRESS,
        clicked_button: 'save_and_finish'
      })

      saveAndFinishAfterDraftUpdates()
    }

    // clicou no botão avançar
    // qualquer step, exceto finish e preview (sempre pelo botão next)
    if (!finishingInteraction && isAnyStepWithValidation) {
      afterNextButton(step)
    }

    // preview e também steps que estão voltando (botão previous)
    if (!finishingInteraction && !isAnyStepWithValidation) {
      setCurrentStepIndex(step)
    }

    window.scrollTo(0, 0)
  }

  return {
    steps,
    totalSteps,
    currentStepIndex,
    uuidInteraction,
    setCurrentStepIndex,
    initialStepIndex,
    changeStep,
    isLoadedSteps,
    isInteractionFinished,
    hasPendingDecisionModels,
    isSaving
  }
}

export default useStep
